import React, { useState, useEffect, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Typography, IconButton,
  Box, Button, TextField, Chip, Divider, CircularProgress, Snackbar, Tooltip,
} from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import Pagination from '@material-ui/lab/Pagination';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import EmailIcon from '@material-ui/icons/Email';
import LinkIcon from '@material-ui/icons/Link';
import RefreshIcon from '@material-ui/icons/Refresh';

import { EvaluationCategoryData } from '../../../services';
import ConfirmationDialog from '../../../sharedComponents/dialog/ConfirmationDialog';

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    backgroundColor: '#1d428a',
    color: '#fff',
    padding: '10px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  dialogClose: { color: '#fff' },
  content: {
    padding: theme.spacing(1.5),
    backgroundColor: '#fafafa',
    height: '65vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  scrollBody: {
    flex: 1,
    overflowY: 'auto',
    minHeight: 0,
    marginRight: theme.spacing(-0.5),
    paddingRight: theme.spacing(0.5),
  },
  intro: { color: '#555', marginBottom: theme.spacing(1.5), fontSize: 13 },
  prospectCard: {
    backgroundColor: '#fff',
    border: '1px solid #dbe4f3',
    borderLeft: '4px solid #1d428a',
    borderRadius: 6,
    padding: theme.spacing(0.75, 1.25),
    marginBottom: theme.spacing(0.75),
    boxShadow: '0 1px 2px rgba(29, 66, 138, 0.06)',
  },
  prospectBadge: {
    display: 'inline-block',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    color: '#1d428a',
    backgroundColor: '#e8eef9',
    borderRadius: 3,
    padding: '1px 6px',
    marginBottom: 2,
  },
  prospectHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  prospectName: { fontWeight: 600, color: '#1d428a', fontSize: 14 },
  prospectMeta: { color: '#666', fontSize: 12 },
  evalChip: {
    backgroundColor: '#fff4e0',
    color: '#b26a00',
    border: '1px solid #f2d19a',
    fontWeight: 600,
  },
  searchToggleBtn: {
    textTransform: 'none',
    color: '#1d428a',
    borderColor: '#9db4dd',
    backgroundColor: '#f3f7fd',
    '&:hover': { backgroundColor: '#e8eef9', borderColor: '#1d428a' },
  },
  hideToggleBtn: {
    textTransform: 'none',
    color: '#6b7280',
    borderColor: '#d1d5db',
    backgroundColor: '#f5f6f7',
    '&:hover': { backgroundColor: '#eceef0', borderColor: '#9ca3af' },
  },
  suggestion: {
    backgroundColor: '#eef6ee',
    border: '1px solid #c6e6c6',
    borderRadius: 4,
    padding: theme.spacing(1),
    marginTop: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  matchArea: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: '#f2f8f8',
    border: '1px solid #cfe6e3',
    borderLeft: '3px solid #0f766e',
    borderRadius: 6,
  },
  matchAreaTitle: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    color: '#0f766e',
    marginBottom: theme.spacing(0.5),
  },
  resultsList: {
    maxHeight: 180,
    overflowY: 'auto',
    marginTop: theme.spacing(0.5),
    marginLeft: theme.spacing(-1),
    marginRight: theme.spacing(-1),
    borderTop: '1px solid #cfe6e3',
  },
  matchRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 1),
    borderBottom: '1px solid #dcebe9',
    transition: 'background-color 0.15s ease',
    '&:last-child': { borderBottom: 'none' },
    '&:hover': { backgroundColor: '#e6f3f1' },
  },
  officialName: { fontWeight: 600, color: '#0f5c56' },
  matchScoreBadge: {
    display: 'inline-block',
    marginLeft: theme.spacing(1),
    padding: '1px 6px',
    borderRadius: 10,
    fontSize: '0.68rem',
    fontWeight: 700,
    verticalAlign: 'middle',
  },
  prospectTopRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  nameField: {
    flex: '1 1 130px',
    backgroundColor: '#fff',
  },
  emailField: {
    flex: '1 1 200px',
    backgroundColor: '#f7f9fc',
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4, 2),
    color: '#777',
  },
  searchInputs: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: theme.spacing(1),
  },
}));

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

const ProspectMappingModal = ({ open, onClose, onMapped }) => {
  const classes = useStyles();

  const PAGE_SIZE = 10;

  const [loading, setLoading] = useState(false);
  const [prospects, setProspects] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  // Per-prospect manual match state keyed by evalProspectUID.
  const [matchState, setMatchState] = useState({});
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [confirm, setConfirm] = useState({ open: false, prospect: null, official: null });
  const [mapping, setMapping] = useState(false);

  const loadProspects = useCallback(async () => {
    setLoading(true);
    setSearch('');
    setPage(1);
    try {
      const data = await EvaluationCategoryData.getUnmappedProspects();
      // Only show prospects that have at least one evaluation attached.
      const filtered = Array.isArray(data)
        ? data.filter((p) => (p.evaluationCount || 0) > 0)
        : [];
      setProspects(filtered);
      setMatchState({});
    } catch (err) {
      setToast({ open: true, message: err?.message || 'Failed to load prospects.', severity: 'error' });
      setProspects([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      loadProspects();
    }
  }, [open, loadProspects]);

  const updateMatchState = (uid, patch) => {
    setMatchState((prev) => ({ ...prev, [uid]: { ...(prev[uid] || {}), ...patch } }));
  };

  const getFirst = (prospect) => {
    const s = matchState[prospect.evalProspectUID] || {};
    return s.firstName !== undefined ? s.firstName : (prospect.firstName || '');
  };

  const getLast = (prospect) => {
    const s = matchState[prospect.evalProspectUID] || {};
    return s.lastName !== undefined ? s.lastName : (prospect.lastName || '');
  };

  const handleSearchOfficials = async (prospect) => {
    const uid = prospect.evalProspectUID;
    const firstName = getFirst(prospect).trim();
    const lastName = getLast(prospect).trim();
    if (!firstName && !lastName) {
      setToast({ open: true, message: 'Enter a first or last name to search.', severity: 'warning' });
      return;
    }
    updateMatchState(uid, { searching: true });
    try {
      const data = await EvaluationCategoryData.matchOfficialsByName(firstName, lastName);
      updateMatchState(uid, { results: Array.isArray(data) ? data : [], searched: true, searching: false });
    } catch (err) {
      updateMatchState(uid, { searching: false });
      setToast({ open: true, message: err?.message || 'Search failed.', severity: 'error' });
    }
  };

  const requestMap = (prospect, official) => {
    setConfirm({ open: true, prospect, official });
  };

  const handleConfirmMap = async (result) => {
    if (!result) {
      setConfirm({ open: false, prospect: null, official: null });
      return;
    }
    const { prospect, official } = confirm;
    setMapping(true);
    try {
      const res = await EvaluationCategoryData.mapProspectToOfficial(
        prospect.evalProspectUID,
        official.officialUID,
      );
      const transferred = res?.transferredCount ?? 0;
      setProspects((prev) => prev.filter((p) => p.evalProspectUID !== prospect.evalProspectUID));
      setToast({
        open: true,
        message: `Mapped ${prospect.firstName || ''} ${prospect.lastName || ''} to ${official.firstName || ''} ${official.lastName || ''}. ${transferred} evaluation(s) transferred.`,
        severity: 'success',
      });
      if (onMapped) onMapped();
    } catch (err) {
      setToast({ open: true, message: err?.message || 'Mapping failed.', severity: 'error' });
    } finally {
      setMapping(false);
      setConfirm({ open: false, prospect: null, official: null });
    }
  };

  const fullName = (first, last) => `${(first || '').trim()} ${(last || '').trim()}`.trim() || '(no name)';

  const getMatchScoreStyle = (score) => {
    if (score >= 85) return { backgroundColor: '#dcfce7', color: '#16a34a' };
    if (score >= 65) return { backgroundColor: '#fef9c3', color: '#ca8a04' };
    return { backgroundColor: '#fee2e2', color: '#dc2626' };
  };

  const searchTerm = search.trim().toLowerCase();
  const filteredProspects = searchTerm
    ? prospects.filter((p) => {
      const name = `${p.firstName || ''} ${p.lastName || ''}`.toLowerCase();
      const email = (p.email || '').toLowerCase();
      return name.includes(searchTerm) || email.includes(searchTerm);
    })
    : prospects;

  const pageCount = Math.ceil(filteredProspects.length / PAGE_SIZE) || 1;
  const currentPage = Math.min(page, pageCount);
  const pagedProspects = filteredProspects.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason === 'backdropClick') return;
          onClose(event, reason);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ style: { maxWidth: 700 } }}
      >
        <DialogTitle disableTypography className={classes.dialogTitle}>
          <Box display="flex" alignItems="center" gridGap={8}>
            <PersonAddIcon />
            <Typography variant="h6">Map Prospects to Officials</Typography>
          </Box>
          <Box>
            <Tooltip title="Refresh">
              <IconButton size="small" className={classes.dialogClose} onClick={loadProspects} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <IconButton size="small" className={classes.dialogClose} onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent className={classes.content}>
          <Typography variant="body2" className={classes.intro}>
            These prospects were added while offline or as a quick entry while evaluating. Map each one to an existing official profile to
            transfer its evaluations. When an email match is found it is suggested automatically; otherwise,
            search by name to select the correct profile.
          </Typography>

          {!loading && prospects.length > 0 && (
            <TextField
              size="small"
              variant="outlined"
              fullWidth
              placeholder="Search prospects by name or email"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              InputProps={{ startAdornment: <SearchIcon fontSize="small" style={{ color: '#777', marginRight: 8 }} /> }}
              style={{ backgroundColor: '#fff', marginBottom: 12 }}
            />
          )}

          <Box className={classes.scrollBody}>
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" py={6}>
                <CircularProgress />
              </Box>
            ) : prospects.length === 0 ? (
              <Box className={classes.emptyState}>
                <Typography variant="h6" gutterBottom>No prospects awaiting mapping</Typography>
                <Typography variant="body2">
                  There are no prospects with evaluations to map.
                </Typography>
              </Box>
            ) : filteredProspects.length === 0 ? (
              <Box className={classes.emptyState}>
                <Typography variant="h6" gutterBottom>No matching prospects</Typography>
                <Typography variant="body2">
                  No prospects match &quot;{search.trim()}&quot;. Try a different name or email.
                </Typography>
              </Box>
            ) : (
              pagedProspects.map((prospect) => {
              const uid = prospect.evalProspectUID;
              const state = matchState[uid] || {};
              const hasEmailMatch = !!prospect.emailMatchOfficialUID;
              return (
                <Box key={uid} className={classes.prospectCard}>
                  <Box className={classes.prospectTopRow}>
                    <span className={classes.prospectBadge}>Prospect</span>
                    <Chip size="small" className={classes.evalChip} label={`${prospect.evaluationCount || 0} evaluation(s)`} />
                  </Box>

                  <Box className={classes.searchInputs}>
                    <TextField
                      size="small"
                      variant="outlined"
                      label="First name"
                      className={classes.nameField}
                      value={getFirst(prospect)}
                      onChange={(e) => updateMatchState(uid, { firstName: e.target.value })}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearchOfficials(prospect)}
                    />
                    <TextField
                      size="small"
                      variant="outlined"
                      label="Last name"
                      className={classes.nameField}
                      value={getLast(prospect)}
                      onChange={(e) => updateMatchState(uid, { lastName: e.target.value })}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearchOfficials(prospect)}
                    />
                    {prospect.email && (
                      <TextField
                        size="small"
                        variant="outlined"
                        label="Email"
                        className={classes.emailField}
                        value={prospect.email}
                        InputProps={{ readOnly: true }}
                      />
                    )}
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      startIcon={state.searching ? <CircularProgress size={14} style={{ color: '#fff' }} /> : <SearchIcon />}
                      onClick={() => handleSearchOfficials(prospect)}
                      disabled={state.searching}
                      style={{ textTransform: 'none' }}
                    >
                      Search
                    </Button>
                  </Box>

                  {hasEmailMatch && (
                    <Box className={classes.suggestion}>
                      <Box display="flex" alignItems="center" gridGap={8}>
                        <EmailIcon fontSize="small" style={{ color: '#059669' }} />
                        <Typography variant="body2">
                          Email match: <strong>{prospect.emailMatchName || 'Official'}</strong>
                        </Typography>
                      </Box>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<LinkIcon />}
                        style={{ backgroundColor: '#059669', color: '#fff', textTransform: 'none' }}
                        onClick={() => requestMap(prospect, {
                          officialUID: prospect.emailMatchOfficialUID,
                          firstName: prospect.firstName,
                          lastName: prospect.lastName,
                        })}
                      >
                        Map
                      </Button>
                    </Box>
                  )}

                  {state.searched && (
                    <Box className={classes.matchArea}>
                      <Typography className={classes.matchAreaTitle}>
                        Match to an existing official
                      </Typography>

                      {(state.results || []).length === 0 && (
                        <Typography variant="body2" style={{ color: '#999', padding: '4px 0' }}>
                          No matching officials found.
                        </Typography>
                      )}

                      {(state.results || []).length > 0 && (
                        <>
                          <Typography variant="caption" style={{ color: '#0f766e', fontWeight: 600, display: 'block', margin: '4px 0' }}>
                            {(state.results || []).length} matching official(s) found. Scroll to see more.
                          </Typography>
                          <Box className={classes.resultsList}>
                            {(state.results || []).map((official) => (
                              <Box key={official.officialUID} className={classes.matchRow}>
                                <Box>
                                  <Typography variant="body2" className={classes.officialName}>
                                    {fullName(official.firstName, official.lastName)}
                                    {typeof official.matchScore === 'number' && (
                                      <span
                                        className={classes.matchScoreBadge}
                                        style={getMatchScoreStyle(official.matchScore)}
                                        title="Name match confidence"
                                      >
                                        {official.matchScore}% match
                                      </span>
                                    )}
                                  </Typography>
                                  <Typography variant="caption" style={{ color: '#777' }}>
                                    {official.email || 'No email'}{official.userTags ? ` • ${official.userTags}` : ''}
                                  </Typography>
                                </Box>
                                <Button
                                  variant="contained"
                                  size="small"
                                  startIcon={<LinkIcon />}
                                  onClick={() => requestMap(prospect, official)}
                                  style={{ textTransform: 'none', backgroundColor: '#0f766e', color: '#fff' }}
                                >
                                  Map
                                </Button>
                              </Box>
                            ))}
                          </Box>
                        </>
                      )}
                    </Box>
                  )}
                </Box>
              );
            })
            )}
          </Box>

          {!loading && filteredProspects.length > PAGE_SIZE && (
            <Box display="flex" justifyContent="center" alignItems="center" pt={1.5} style={{ borderTop: '1px solid #eee' }}>
              <Pagination
                count={pageCount}
                page={currentPage}
                onChange={(e, value) => setPage(value)}
                color="primary"
                size="small"
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} color="default" style={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog
        open={confirm.open}
        title="Confirm mapping"
        isLoading={mapping}
        txtConfirm="Map & Transfer"
        txtCancel="Cancel"
        handleResponse={handleConfirmMap}
        message={
          confirm.prospect && confirm.official ? (
            <Box>
              <Typography variant="body2" gutterBottom>
                Transfer all evaluations from the prospect
                {' '}<strong>{fullName(confirm.prospect.firstName, confirm.prospect.lastName)}</strong>
                {' '}to the official
                {' '}<strong>{fullName(confirm.official.firstName, confirm.official.lastName)}</strong>?
              </Typography>
              <Typography variant="body2" style={{ color: 'rgba(0, 0, 0, 0.87)' }}>
                The temporary prospect record will be removed. This action cannot be undone.
              </Typography>
            </Box>
          ) : ''
        }
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={5000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setToast((t) => ({ ...t, open: false }))} severity={toast.severity}>
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ProspectMappingModal;
